import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import StyledScrollSlider from 'components/molecules/Sliders/ScrollSlider/ScrollSlider.styles';
import utils from 'helpers/utils';
import dom from 'helpers/dom';

const ScrollSlider = React.forwardRef((props, ref) => {
  const {
    orientation,
    keyStep,
    ...innerProps
  } = useComponentProps({
    orientation: 'horizontal',
    keyStep: 100,
    ...props
  }, 'ScrollSlider', {
    variable: ['orientation'],
    static: ['visible', 'scrolled']
  });

  const innerRef = useRef(null);
  const [internalState, setInternalState] = useState({
    scrollElement: null,
    scroll: null,
    scrolled: false,
    drag: null
  });

  const vertical = orientation === 'vertical';
  const max = internalState.scroll?.max ?? 0;
  const position = internalState.drag ?? internalState.scroll?.value ?? 0;

  const scrollElement = internalState.scrollElement;
  const measure = useCallback(() => {
    if (scrollElement) {
      const root = scrollElement === window ? document.documentElement : scrollElement;
      setInternalState(utils.updater({
        scroll: vertical ?
          {value: root.scrollTop, max: root.scrollHeight - root.clientHeight, page: root.clientHeight} :
          {value: root.scrollLeft, max: root.scrollWidth - root.clientWidth, page: root.clientWidth},
        scrolled: true
      }, true));
    }
  }, [scrollElement, vertical]);

  const scrollSlider = useMemo(() => ({
    refs: {
      ref: innerRef
    },
    state: {
      ...internalState
    },
    measure
  }), [internalState, measure]);

  useImperativeHandle(ref, () => scrollSlider);

  const sliderEl = innerRef.current;
  useEffect(() => {
    const element = () => {
      const scrollElement = dom.getScrollElement(innerRef.current, !vertical, Boolean(vertical));
      if (scrollElement) {
        setInternalState((current) => {
          if (current.scrollElement !== scrollElement) {
            return {...current, scrollElement};
          } else {
            return current;
          }
        });
      }
    }

    return utils.observeRetry(element, 8);
  }, [sliderEl, vertical]);

  useEffect(() => {
    if (scrollElement) {
      return utils.observeScroll(scrollElement, measure, {direction: vertical ? 'vertical' : 'horizontal'});
    }
  }, [scrollElement, vertical, measure]);

  innerProps.className = utils.flattenClassName(innerProps.className, {
    visible: max > 0,
    scrolled: internalState.scrolled && internalState.drag === null
  });

  return <StyledScrollSlider ref={innerRef} {...innerProps}
                             min={0}
                             max={max}
                             orientation={orientation}
                             value={vertical ? max - position : position}
                             onChange={(e, v) => {
                               // vertical sliders run bottom to top, scrolling runs top to bottom
                               const value = vertical ? max - v : v;
                               // MUI has one step for dragging and keys, so a key only gives the direction
                               const step = (e.key?.startsWith('Page') || e.shiftKey) ? internalState.scroll.page : keyStep;
                               const drag = (e.key?.startsWith('Arrow') || e.key?.startsWith('Page')) ?
                                 Math.min(max, Math.max(0, position + (Math.sign(value - position) * step))) :
                                 value;
                               setInternalState(utils.updater({drag, scrolled: false}, true));
                               scrollElement?.scrollTo?.({[vertical ? 'top' : 'left']: drag});
                             }}
                             onChangeCommitted={() => setInternalState(utils.updater({drag: null, scrolled: false}, true))}/>
});

ScrollSlider.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  keyStep: PropTypes.number
};

export default ScrollSlider;
